import { useMemo } from 'react';

import { isCypressEnvironment } from '../helper';

type TimingConfig = {
  duration?: number;
  delay?: number;
  initialState?: string;
};

/**
 * A hook to handle timing-related functionality differently in test environments.
 * This centralises the logic for skipping animations and delays in Cypress.
 *
 * @example
 * ```tsx
 * const { duration, delay, animationState } = useTestEnvironmentTiming({
 *   duration: 0.4,
 *   delay: delayOrder / 5,
 *   initialState: 'hidden'
 * });
 * ```
 */
export const useTestEnvironmentTiming = ({
  duration = 0,
  delay = 0,
  initialState = 'hidden',
}: TimingConfig): {
  duration: number;
  delay: number;
  animationState: string;
} =>
  useMemo(
    () => ({
      duration: isCypressEnvironment() ? 0 : duration,
      delay: isCypressEnvironment() ? 0 : delay,
      animationState: isCypressEnvironment() ? 'show' : initialState,
    }),
    [duration, delay, initialState],
  );
